import { useCallback, useEffect, useState } from "react";
import { HealthResponse, api, isError } from "../api";

/** Overview health strip (F32).
 *
 *  `/health` knows whether the DB, bridge, scheduler and keystore are
 *  each usable, but nothing surfaced it: the dashboard could look fine
 *  while the system was unable to trade. Each component says what is
 *  wrong and what to do about it, because "scheduler: not ok" on its own
 *  sends you to the logs. */
export function SystemHealthPanel() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.health();
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setError(null);
    setHealth(r);
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  if (error) {
    return (
      <section className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
        Backend unreachable: {error}
      </section>
    );
  }
  if (health === null) return null;

  const c = health.components;
  const items = [
    {
      key: "db",
      label: "Database",
      ok: c.db.ok,
      detail: c.db.ok ? "connected" : c.db.error ?? "unreachable",
    },
    {
      key: "bridge",
      label: "MT5 bridge",
      ok: c.bridge.ok,
      detail: !c.bridge.connected
        ? "offline — start it on the MT5 machine"
        : c.bridge.mt5_login == null
          ? "connected, no account — save credentials on Trading account"
          : `${c.bridge.mt5_login} · ${c.bridge.mt5_server ?? ""}`,
    },
    {
      key: "scheduler",
      label: "Scheduler",
      ok: c.scheduler.ok,
      detail: c.scheduler.running
        ? `${c.scheduler.jobs?.length ?? 0} jobs`
        : "not running — news, exits and backups are stalled",
    },
    {
      key: "keystore",
      label: "Secrets",
      ok: c.keystore.ok,
      detail: c.keystore.ok ? "encryption key loaded" : "no encryption key",
    },
  ];

  return (
    <section
      className={`rounded-lg border px-3 py-2 ${
        health.ready ? "border-border bg-surface" : "border-warn/40 bg-warn/5"
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
          {health.ready ? "System ready" : "Not ready to trade"}
        </span>
        {items.map((item) => (
          <span key={item.key} className="inline-flex items-baseline gap-1.5 text-xs">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                item.ok ? "bg-success" : "bg-danger"
              }`}
              aria-hidden
            />
            <span className="text-fg">{item.label}</span>
            <span className="font-mono text-fg-muted">{item.detail}</span>
          </span>
        ))}
      </div>
    </section>
  );
}
